import { FileIcon } from "../file-icon/FileIcon";
import { Scrollbar } from "../scrollbar/Scrollbar";
import { PartialOptions } from "overlayscrollbars";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SourceFileView } from "../../types";
import { getFileName } from "../../utils/utils";
import { IconButton, Tab, Tabs } from "@itwin/itwinui-react";
import styles from "./TabNavigation.module.css";
import "@vscode/codicons/dist/codicon.css";

export interface FileTabOptions {
  items: SourceFileView[],
  onItemChanged?: (filePath: string) => void;
  onItemClosed?: (filePath: string) => void;
}

const scrollOptions: PartialOptions = {
  overflow: {
    x: "scroll",
    y: "hidden"
  },
  scrollbars: {
    visibility: "auto",
    autoHide: "leave",
    autoHideDelay: 100,
  },
};

const MIDDLE_BUTTON_ID = 1;

const preventAutoScroll = (event?: React.MouseEvent) => {
  // Let other mouse button options propogate
  if (event?.button !== MIDDLE_BUTTON_ID)
    return;

  // Cancel middle button event
  event?.preventDefault();
  event?.stopPropagation();
};

const getFilePath = (fileView: SourceFileView) => fileView.filePathRenamed ?? fileView.filePath;

export const TabNavigation = ({ items, onItemChanged, onItemClosed }: FileTabOptions) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const activeIndex = items.findIndex((item) => item.active);
    setIndex(activeIndex === -1 ? 0 : activeIndex);
  }, [items])

  const onTabSelected = useCallback((index: number) => {
    setIndex(index);
    onItemChanged && onItemChanged(getFilePath(items[index]));
  }, [onItemChanged, items])

  const getLabel = useCallback((item: SourceFileView) => {
    return <TabLabel filePath={getFilePath(item)} dirty={item.dirty} onClose={onItemClosed} alwaysVisible={item.alwaysVisible} />;
  }, [onItemClosed]);

  const labels = useMemo(() => items.map((item) => {
    return (
      <Tab
        key={getFilePath(item)}
        onMouseDown={(event?: React.MouseEvent) => {
          if (event?.button === MIDDLE_BUTTON_ID && onItemClosed && !item.alwaysVisible)
            onItemClosed(getFilePath(item))
        }}
        className={[styles.tabItem, item.readOnly ? styles.readOnly : ""].join(" ")}
        label={getLabel(item)} />
    );
  }), [items]);

  return (
    <Scrollbar size="small" element="div" options={scrollOptions} defer onMouseDown={preventAutoScroll}>
      <Tabs
        labels={labels}
        wrapperClassName={styles.tabsContainer}
        tabsClassName={styles.tabItems}
        activeIndex={index}
        type="default"
        onTabSelected={onTabSelected} />
    </Scrollbar>);
}

const TabLabel = ({ filePath, dirty, onClose, alwaysVisible }: { filePath: string, dirty?: boolean, onClose?: (filePath: string) => void, alwaysVisible?: boolean }) => {

  const onCloseClick = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onClose && onClose(filePath);
  }

  return (
    <div className={styles.tabLabel}>
      <FileIcon filePath={filePath} />
      <div className={styles.tabLabelCompensation}>{getFileName(filePath)}</div>
      {!alwaysVisible
        ? <div className={styles.closeBtnContainer}>
          {dirty && <div className={["codicon", "codicon-circle-filled", styles.btnDirty].join(" ")} />}
          <IconButton as="div" styleType="borderless" size="small" className={styles.closeBtn} onClick={onCloseClick}><div className="codicon codicon-close" /></IconButton>
        </div>
        : <div className={styles.closeBtnPlaceholder}></div>
      }
    </div >
  )
}

